// Pure per-button wire-component builder shared by the COMPLEX and LTO sends the Standard panel
// builds. No shared BUTTON_TYPE/URL_TYPE enum import — same raw wire-string-literal convention
// ../carousel/buildCarouselTemplateJson.js already uses (the module can't import
// pages/Templates/Form/state.js; see ../README.md).
//
// Contracts verified directly against Enterprise (task brief's "Verified send contracts"), not
// copied from the shared library's own COMPLEX code — that code sends UPPERCASE sub_type
// (TemplateMessaging.tsx's handleButtonVariableSelection: `sub_type: button?.type`); lowercase is
// what Enterprise actually expects.
//
// buttons: TemplateButtonInfo[] (../shared/templateModel.js's describeTemplate().buttons —
// [{index, type, urlType, text, url}]).
// values: { [buttonIndex]: string } — the one free-text value that button's input collects, for
// whichever of COPY_CODE / URL+DYNAMIC / URL+DYNAMIC_LINK_TRACK it is (templateModel.js's buttonInputKind).
import { buttonInputKind } from '../shared/templateModel';

/**
 * @param {import('../shared/templateModel').TemplateButtonInfo[]} buttons
 * @param {Object.<number, string>} values
 * @returns {object[]} wire `components` entries — one per button that needs one. Static URL,
 *   phone and quick-reply buttons contribute nothing on either side, same skip rule as
 *   buildCarouselTemplateJson.js.
 */
export function buildButtonComponents(buttons, values) {
  return (buttons || []).reduce((components, button) => {
    const { index, type } = button || {};
    const value = (values && values[index]) || '';
    const kind = buttonInputKind(button);

    if (kind === 'code') {
      components.push({
        type: 'button', sub_type: 'copy_code', index, parameters: [{ type: 'coupon_code', coupon_code: value }],
      });
    } else if (kind === 'suffix' || kind === 'link') {
      // For a tracked link ('link'), sub_type 'url' is assumed by analogy with the suffix case (both are
      // type URL buttons on the wire) — unverified: the single-button case sends ctaButtonUrl instead and
      // never reaches a components array, so this specific shape (a link-tracked URL living inside a
      // COMPLEX/LTO components array) has no confirmed live test yet.
      components.push({
        type: 'button', sub_type: 'url', index, parameters: [{ type: 'text', text: value }],
      });
    } else if (type === 'VOICE_CALL') {
      // Always included, no parameters — Enterprise rejects the whole send when this is the last
      // button and its component is missing.
      components.push({ type: 'button', sub_type: 'voice_call', index });
    }
    // STATIC URL / PHONE_NUMBER / QUICK_REPLY (and anything else) fall through untouched.

    return components;
  }, []);
}

/**
 * Inverse of buildButtonComponents, for restore.
 * @param {object[]} components - saved waTemplateJson.components
 * @param {import('../shared/templateModel').TemplateButtonInfo[]} buttons
 * @returns {Object.<number, string>}
 */
export function parseButtonComponents(components, buttons) {
  const wire = Array.isArray(components) ? components : [];
  const values = {};
  (buttons || []).forEach((button) => {
    if (buttonInputKind(button) === null) return;
    const { index } = button;
    const match = wire.find((component) => component?.type === 'button' && component?.index === index);
    const param = match?.parameters?.[0];
    values[index] = (param?.coupon_code ?? param?.text ?? '') || '';
  });
  return values;
}
